// Ultimate skill concept catalogue. See ADR 0007 and docs/03-jobs-skills §3.11.
//
// This module pins the *concept layer* of all 44 jobs' ultimate skills:
//   - skillId, Japanese name, 1-2 line concept, effect-direction tags,
//     strength class, and provisional cooldown.
// The actual `Skill` / `Effect` construction (damage coefficients, status
// values, placeable templates etc.) happens in C.5 equipment-skill session.
//
// Tier -> strengthClass invariant (ADR 0007 §2.1):
//   T1 = 'weak' / T2 = 'weak-sub' / T3 = 'mid' / T4 = 'strong'
// T2 special 5 jobs share strengthClass 'weak-sub' but target the upper
// numeric band (×1.15-1.20) per ADR 0007 §2.1 ("T2.5" bias, documentation
// only - no enum extension).
//
// Magic-system jobs encode the rule "skill element follows equipped weapon"
// (ADR 0007 §3): fortune-teller / mage / archmage / magus-knight /
// magic-archer / spellblade / dragon-knight. Exceptions: summoner /
// shadow-hunter / arch-summoner / grand-sage.
/** Set of canonical effect kinds, used for runtime validation. */
export const ULTIMATE_EFFECT_KINDS = new Set([
    'damage',
    'area',
    'buff',
    'debuff',
    'summon',
    'placeable',
    'heal',
    'mobility',
    'control',
]);
/** Allowed cooldownTicks values per ADR 0007 §2.3. */
export const ULTIMATE_COOLDOWN_TICKS_VALUES = [900, 1800, 3600];
// --- Tier 1 (6 jobs) — strengthClass: weak ---
const T1 = [
    {
        jobId: 'warrior',
        skillId: 'ult.warrior.greatcleave',
        name: '大車輪斬',
        concept: '前方扇形に物理ダメージを与える、戦士の素朴な力技。',
        effectKinds: ['damage', 'area'],
        strengthClass: 'weak',
        cooldownTicks: 900,
    },
    {
        jobId: 'fortune-teller',
        skillId: 'ult.fortune-teller.starcast',
        name: '占星魔導',
        concept: 'タロット風カードを 1 枚引き、装備武器の属性 (火/氷/雷/土/光/闇) の小魔法弾を敵単体に放ち、命中時に対応属性の小デバフを付与する。',
        effectKinds: ['damage', 'debuff'],
        strengthClass: 'weak',
        cooldownTicks: 1800,
    },
    {
        jobId: 'priest-novice',
        skillId: 'ult.priest-novice.prayer',
        name: '祈り',
        concept: '味方単体を中回復し、短時間の小バリアを付与する。',
        effectKinds: ['heal', 'buff'],
        strengthClass: 'weak',
        cooldownTicks: 1800,
    },
    {
        jobId: 'thief',
        skillId: 'ult.thief.backstab',
        name: '背討ち',
        concept: '短距離 dash で背後に回り込み、確クリ斬撃で単体に大ダメージを与える。',
        effectKinds: ['damage', 'mobility'],
        strengthClass: 'weak',
        cooldownTicks: 900,
    },
    {
        jobId: 'entertainer',
        skillId: 'ult.entertainer.encore',
        name: 'アンコール',
        concept: '味方全体に短時間の攻撃速度小バフを付与する。',
        effectKinds: ['buff', 'area'],
        strengthClass: 'weak',
        cooldownTicks: 1800,
    },
    {
        jobId: 'hunter',
        skillId: 'ult.hunter.snare',
        name: 'くくり罠',
        concept: '接触型の罠を 1 個設置し、踏んだ敵に小ダメージと移動低下を与える。',
        effectKinds: ['placeable', 'control'],
        strengthClass: 'weak',
        cooldownTicks: 1800,
    },
];
// --- Tier 2 (11 jobs) — strengthClass: weak-sub ---
// Default 6: bias toward central numeric band (×1.05-1.10).
// Special 5: bias toward upper band (×1.15-1.20) per "T2.5" doc bias.
const T2 = [
    // Default 6
    {
        jobId: 'knight',
        skillId: 'ult.knight.bulwark',
        name: '鉄壁の構え',
        concept: '自身に被ダメ大幅軽減バフを得つつ、周囲敵を短時間挑発する。',
        effectKinds: ['buff', 'control'],
        strengthClass: 'weak-sub',
        cooldownTicks: 1800,
    },
    {
        jobId: 'mage',
        skillId: 'ult.mage.spell-blast',
        name: '大魔法弾',
        concept: '単体に大型魔法弾 (装備武器属性) を撃ち、命中時に中範囲スプラッシュダメージを発生させる。',
        effectKinds: ['damage', 'area'],
        strengthClass: 'weak-sub',
        cooldownTicks: 1800,
    },
    {
        jobId: 'cleric',
        skillId: 'ult.cleric.benediction',
        name: '祝祷',
        concept: '味方単体を中回復し、状態異常 1 種を浄化する。',
        effectKinds: ['heal'],
        strengthClass: 'weak-sub',
        cooldownTicks: 900,
    },
    {
        jobId: 'assassin',
        skillId: 'ult.assassin.shadow-step',
        name: '影縫い',
        concept: '短距離 dash で背後に瞬間移動し、単体に高威力ダメージと出血を与える。',
        effectKinds: ['mobility', 'damage', 'debuff'],
        strengthClass: 'weak-sub',
        cooldownTicks: 900,
    },
    {
        jobId: 'bowmaster',
        skillId: 'ult.bowmaster.eagle-eye',
        name: '鷹の眼',
        concept: '強力な単発狙撃で大ダメージを与え、命中後しばらく自身の通常攻撃速度が上昇する。',
        effectKinds: ['damage', 'buff'],
        strengthClass: 'weak-sub',
        cooldownTicks: 1800,
    },
    {
        jobId: 'bard',
        skillId: 'ult.bard.heroic-chorus',
        name: '英雄合唱',
        concept: '味方全体に攻撃力バフと持続小回復バフを同時に付与する。',
        effectKinds: ['buff', 'heal', 'area'],
        strengthClass: 'weak-sub',
        cooldownTicks: 1800,
    },
    // Special 5 (T2.5 bias — upper weak-sub band)
    {
        jobId: 'samurai',
        skillId: 'ult.samurai.counter-stance',
        name: '無念返し',
        concept: '短時間カウンター構えに入り、被弾時に自身の攻撃モーションで強力な反撃斬撃を放ち、初撃の敵を短スタンさせる。',
        effectKinds: ['buff', 'damage', 'control'],
        strengthClass: 'weak-sub',
        cooldownTicks: 1800,
    },
    {
        jobId: 'summoner',
        skillId: 'ult.summoner.dual-sigil',
        name: '双印召喚',
        concept: 'ランダムに 4 属性 (火/水/風/土) のいずれかを選び、その属性の小型精霊 2 体を同時召喚し、味方全体にその属性に対応した小バフを付与する。',
        effectKinds: ['summon', 'buff', 'area'],
        strengthClass: 'weak-sub',
        cooldownTicks: 1800,
    },
    {
        jobId: 'beast-tamer',
        skillId: 'ult.beast-tamer.warden-howl',
        name: '護りの咆哮',
        concept: '相棒獣 (戦闘開始時から常駐、再召喚なし) が自身周囲の敵をノックバック攻撃で押し戻し出血を付与し、自身に攻撃速度バフを得る。',
        effectKinds: ['damage', 'mobility', 'debuff', 'buff'],
        strengthClass: 'weak-sub',
        cooldownTicks: 1800,
    },
    {
        jobId: 'phantom-thief',
        skillId: 'ult.phantom-thief.heist-time',
        name: '大盗時間',
        concept: '対象 1 体から ATK/DEF を短時間盗み自身に上乗せし、自身が短時間ステルス状態に入る。',
        effectKinds: ['debuff', 'buff', 'control'],
        strengthClass: 'weak-sub',
        cooldownTicks: 1800,
    },
    {
        jobId: 'ascetic-monk',
        skillId: 'ult.ascetic-monk.self-sacrifice',
        name: '捨身の浄化',
        concept: '自身 HP を固定値消費し (即時)、短いディレイの後に味方範囲を完全 cleanse して中回復する。',
        effectKinds: ['heal', 'area'],
        strengthClass: 'weak-sub',
        cooldownTicks: 1800,
    },
];
// --- Tier 3 (22 jobs) — strengthClass: mid ---
const T3 = [
    // Section A: Escalator endpoints (default progression) - 6 jobs
    {
        jobId: 'paladin',
        skillId: 'ult.paladin.holy-aegis',
        name: '光の聖域',
        concept: '自身周囲に光の aura-zone を設置する。zone 内の味方は被ダメ大幅軽減と短時間ヘイト分散を得て、被弾時に反射ダメージを返す。',
        effectKinds: ['placeable', 'buff', 'area', 'damage'],
        strengthClass: 'mid',
        cooldownTicks: 3600,
    },
    {
        jobId: 'archmage',
        skillId: 'ult.archmage.meteor-shower',
        name: 'メテオシャワー',
        concept: '装備武器属性の多段隕石を広範囲に降らせ、属性別の二次効果ゾーン (火=大火球+燃焼/氷=凍結+鈍足/雷=連鎖落雷+麻痺/土=岩塊+防御低下/光=神光+暗影解除/闇=闇腐食+暗影付与) を残す。',
        effectKinds: ['damage', 'area', 'debuff', 'placeable'],
        strengthClass: 'mid',
        cooldownTicks: 3600,
    },
    {
        jobId: 'high-priest',
        skillId: 'ult.high-priest.divine-grace',
        name: '神の恵み',
        concept: '味方全体を大回復し、短時間シールドを付与しつつ、各味方の状態異常 1 種を浄化する。',
        effectKinds: ['heal', 'buff', 'area'],
        strengthClass: 'mid',
        cooldownTicks: 3600,
    },
    {
        jobId: 'ninja',
        skillId: 'ult.ninja.shadow-execution',
        name: '影遁・断罪',
        concept: '自身が短時間ステルスで保身に入り、分身 4 体 (時間消滅) を生成する。分身が手裏剣で対象範囲にダメージを放った後、分身たちが dash で接敵して斬撃する。',
        effectKinds: ['summon', 'damage', 'area', 'buff'],
        strengthClass: 'mid',
        cooldownTicks: 1800,
    },
    {
        jobId: 'bow-saint',
        skillId: 'ult.bow-saint.piercing-arrow',
        name: '神弓貫通',
        concept: '直線範囲を貫通する確クリ大ダメージの神矢を放ち、命中した全敵に脆弱中デバフを付与する。',
        effectKinds: ['damage', 'debuff', 'area'],
        strengthClass: 'mid',
        cooldownTicks: 1800,
    },
    {
        jobId: 'gakusei',
        skillId: 'ult.gakusei.elemental-symphony',
        name: '元素交響曲',
        concept: '一定時間持続する元素演奏を展開する。属性が時間で切り替わり (火→土→雷→光→闇→氷)、味方全員の通常攻撃に切替属性のダメージ追加と属性別バフ (火=ATK/氷=クリ率/雷=AS/土=DR/光=cleanse 1 種/闇=HP 吸収) を付与する。',
        effectKinds: ['damage', 'buff', 'area'],
        strengthClass: 'mid',
        cooldownTicks: 3600,
    },
    // Section B: Cross default+default - 9 jobs
    {
        jobId: 'magus-knight',
        skillId: 'ult.magus-knight.runic-vortex',
        name: 'ルーンの渦',
        concept: '自身に魔法ダメ無効バリアを展開しつつ、周囲の離れた敵を自身方向に引き寄せ、引き寄せ時に装備武器属性のダメージを与える。',
        effectKinds: ['mobility', 'damage', 'buff', 'area'],
        strengthClass: 'mid',
        cooldownTicks: 1800,
    },
    {
        jobId: 'valkyrie',
        skillId: 'ult.valkyrie.battle-blessing',
        name: '戦乙女の祝福',
        concept: '自身に DR バフと強ヘイト集約を得て、被ダメージの一部を味方全体の回復に変換する。',
        effectKinds: ['buff', 'control', 'heal', 'area'],
        strengthClass: 'mid',
        cooldownTicks: 3600,
    },
    {
        jobId: 'sage',
        skillId: 'ult.sage.cleansing-light',
        name: '浄化光',
        concept: '範囲に光属性魔法ダメージを与え、同範囲の味方を中回復しつつ状態異常 1 種を浄化する。',
        effectKinds: ['damage', 'heal', 'area'],
        strengthClass: 'mid',
        cooldownTicks: 1800,
    },
    {
        jobId: 'inquisitor',
        skillId: 'ult.inquisitor.holy-chains',
        name: '聖鎖',
        concept: '敵 1 体を聖なる鎖で拘束し移動不能にする。拘束中、味方が拘束敵にダメージを与えるたびに味方が小回復する。',
        effectKinds: ['control', 'heal'],
        strengthClass: 'mid',
        cooldownTicks: 1800,
    },
    {
        jobId: 'magic-archer',
        skillId: 'ult.magic-archer.arcane-volley',
        name: '魔矢連射',
        concept: '数本の追尾魔矢を放ち、命中対象を中心に小範囲属性ダメージと属性別デバフ (装備武器属性に準ずる) を発生させる。',
        effectKinds: ['damage', 'area', 'debuff'],
        strengthClass: 'mid',
        cooldownTicks: 1800,
    },
    {
        jobId: 'shadow-hunter',
        skillId: 'ult.shadow-hunter.elemental-snare',
        name: '元素罠',
        concept: 'ランダムに 1 属性を選び、その属性の罠を周囲敵の足元に短距離跳ばして自動配置する。罠作動時に属性別の効果 (火=燃焼/氷=鈍足/雷=麻痺/土=防御低下/光=暗影解除+味方小回復/闇=暗影付与) を発動する。',
        effectKinds: ['placeable', 'damage', 'area', 'control', 'debuff'],
        strengthClass: 'mid',
        cooldownTicks: 1800,
    },
    {
        jobId: 'enchanter',
        skillId: 'ult.enchanter.surge-empower',
        name: '魔導過励起',
        concept: '味方単体に短時間 (15-20 秒) の魔力過励起バフを付与する。バフ持続中、対象の ATK/魔法力/クリ率が中バフ、通常攻撃がクリ時に小範囲魔法爆発を追加発生する。',
        effectKinds: ['buff', 'damage', 'area'],
        strengthClass: 'mid',
        cooldownTicks: 1800,
    },
    {
        jobId: 'astrologer',
        skillId: 'ult.astrologer.seven-stars',
        name: '七星祝福',
        concept: '7 つの星を放ち、味方同士を反射しながら進む。反射ごとに対象味方に回復+ATK+クリ率のバフをスタックする (各星最大 5 回反射)。',
        effectKinds: ['heal', 'buff', 'area'],
        strengthClass: 'mid',
        cooldownTicks: 3600,
    },
    {
        jobId: 'divine-archer',
        skillId: 'ult.divine-archer.holy-shower',
        name: '聖雨',
        concept: '光属性の小矢を大量発射する。敵命中で光ダメージを与えた後、矢が光オーブに変化して味方に向かい、到達時に回復と小バフ (HP/防御等) を付与する。',
        effectKinds: ['damage', 'area', 'heal', 'buff'],
        strengthClass: 'mid',
        cooldownTicks: 1800,
    },
    // Section C: Cross default+special - 6 jobs
    {
        jobId: 'spellblade',
        skillId: 'ult.spellblade.runic-strike',
        name: '魔剣斬',
        concept: '装備武器の属性を帯びた斬撃を 1 発飛ばし、命中敵にダメ+ノックバック+脆弱を付与する。CD が短く繰り返し放つ rotation 型。',
        effectKinds: ['damage', 'mobility', 'debuff'],
        strengthClass: 'mid',
        cooldownTicks: 900,
    },
    {
        jobId: 'dragon-knight',
        skillId: 'ult.dragon-knight.dragon-breath',
        name: '竜の息吹',
        concept: '相棒の竜 (常駐) が前方ラインに装備属性ブレスで大ダメージと属性別デバフを与える。竜と自身に短時間 DR と攻撃強化バフを付与する。',
        effectKinds: ['damage', 'area', 'buff', 'debuff'],
        strengthClass: 'mid',
        cooldownTicks: 1800,
    },
    {
        jobId: 'necromancer',
        skillId: 'ult.necromancer.bone-swarm',
        name: '骨の群れ',
        concept: 'スケルトン 5 体 (近接・低 HP・短寿命) を召喚する。スケルトンは死亡時に周囲へデバフを撒き散らし、短サイクルで群れを回し続ける運用を促す。',
        effectKinds: ['summon', 'damage', 'debuff', 'area'],
        strengthClass: 'mid',
        cooldownTicks: 1800,
    },
    {
        jobId: 'druid',
        skillId: 'ult.druid.wildwarden',
        name: '森の番人',
        concept: '樹霊 1 体を召喚する。樹霊は周囲にリジェネエリアをまとい、味方を継続回復しつつ周囲に小バフ胞子をばら撒く。',
        effectKinds: ['summon', 'heal', 'buff', 'area'],
        strengthClass: 'mid',
        cooldownTicks: 3600,
    },
    {
        jobId: 'trickster',
        skillId: 'ult.trickster.mind-game',
        name: '心理戦',
        concept: '敵全体にランダム 1 種の状態異常 (混乱/暗闇/沈黙) を付与しつつ、味方全体にランダム 1 種のバフ (攻撃/防御/速度) を付与する。',
        effectKinds: ['debuff', 'buff', 'area', 'control'],
        strengthClass: 'mid',
        cooldownTicks: 1800,
    },
    {
        jobId: 'puppet-master',
        skillId: 'ult.puppet-master.dual-marionette',
        name: '双傀儡',
        concept: '対となる 2 体の傀儡 (近接型・遠距離型) を召喚し、自身と各傀儡を魔法糸で連結する。近接傀儡はヘイト引き受け+近距離攻撃で敵にダメ+デバフ、遠距離傀儡は遠距離攻撃で敵にダメ+味方バフを放出する。魔法糸に味方が触れると追加バフが付与される。',
        effectKinds: ['summon', 'damage', 'debuff', 'buff'],
        strengthClass: 'mid',
        cooldownTicks: 3600,
    },
    // Section D: Cross special+special - 1 job
    {
        jobId: 'arch-summoner',
        skillId: 'ult.arch-summoner.seven-realms',
        name: '七界召喚',
        concept: '物理+6 属性の 7 種からランダムに 2 体の精霊を召喚し、自身と各召喚体に属性別バフを存続中ずっと付与する。再詠唱で 2 体ずつ追加され、頭数が増えるほど強化される累積召喚型。',
        effectKinds: ['summon', 'buff', 'area'],
        strengthClass: 'mid',
        cooldownTicks: 1800,
    },
];
// --- Tier 4 (5 jobs) — strengthClass: strong ---
const T4 = [
    {
        jobId: 'sword-saint',
        skillId: 'ult.sword-saint.zero-domain',
        name: '無刃境',
        concept: '5 秒間「剣域」を展開し、域内の敵攻撃を大幅減衰しつつ蓄積する。終了時に蓄積した総ダメージを全方位の真空斬として返す。発動中は自身 DR 上昇、敵は命中/AS 低下。',
        effectKinds: ['damage', 'area', 'control', 'buff', 'debuff'],
        strengthClass: 'strong',
        cooldownTicks: 3600,
    },
    {
        jobId: 'grand-sage',
        skillId: 'ult.grand-sage.cosmic-order',
        name: '万象の理',
        concept: '全 7 属性 (物理+6) のエネルギーを同時に解き放つ。敵全体に範囲魔法ダメージと脆弱中を与え、味方全体に大回復と multi-stat バフ (ATK/魔力/AS/DR) を付与する。',
        effectKinds: ['damage', 'heal', 'buff', 'debuff', 'area'],
        strengthClass: 'strong',
        cooldownTicks: 3600,
    },
    {
        jobId: 'astral-archer',
        skillId: 'ult.astral-archer.starfall',
        name: '星辰連弓',
        concept: '上空に星霊 (中型) を 1 体召喚する。星霊が広範囲に流星雨で多段大ダメージを降らせ、味方全体に攻速とクリ率バフを付与し、星屑が地面に持続ダメージゾーンを残す。',
        effectKinds: ['summon', 'damage', 'buff', 'area', 'placeable'],
        strengthClass: 'strong',
        cooldownTicks: 3600,
    },
    {
        jobId: 'dark-lord',
        skillId: 'ult.dark-lord.abyssal-throne',
        name: '奈落王座',
        concept: '戦場に「奈落の王座」を設置し着座する。王座存続中、周囲敵を周期吸引して呪詛を蓄積し、蓄積段階で魔将を段階召喚する。本人は超 DR と挑発磁場を得る。',
        effectKinds: ['placeable', 'summon', 'control', 'debuff', 'buff', 'damage'],
        strengthClass: 'strong',
        cooldownTicks: 3600,
    },
    {
        jobId: 'saint',
        skillId: 'ult.saint.aegis-covenant',
        name: '聖約の盾',
        concept: '聖十字アンカーを 3 点設置し三角領域を形成する。領域内味方は被ダメ無効化チャージを共有 (合計量制) し、超過分は回復に変換される。敵は領域侵入で脆弱と鈍化を受ける。',
        effectKinds: ['buff', 'heal', 'placeable', 'control', 'debuff'],
        strengthClass: 'strong',
        cooldownTicks: 3600,
    },
];
/** Canonical 44-entry list. Order: T1 -> T2 -> T3 -> T4. */
export const ULTIMATE_CONCEPTS = [...T1, ...T2, ...T3, ...T4];
export const ULTIMATE_CONCEPT_BY_JOB_ID = new Map(ULTIMATE_CONCEPTS.map((u) => [u.jobId, u]));
export const ULTIMATE_CONCEPT_BY_SKILL_ID = new Map(ULTIMATE_CONCEPTS.map((u) => [u.skillId, u]));
//# sourceMappingURL=ultimates.js.map